/**
 * Standalone Recently Viewed Products — Shopify
 *
 * Drop-in script that tracks which products a visitor views and renders
 * them back into any container on any page.  Zero theme dependencies.
 *
 * Usage (minimal):
 *   <script src="{{ 'recently-viewed.js' | asset_url }}" defer></script>
 *   <div id="recently-viewed-products"></div>
 *
 * Usage (configured):
 *   <script>
 *     window.RecentlyViewedConfig = {
 *       limit:            6,
 *       maxInMemory:      10,
 *       containerId:      'recently-viewed-products',
 *       sectionId:        'api-product-grid-item',
 *       routesRoot:       '/en/',          // multi-language stores
 *       cookieName:       'shopify_recently_viewed',
 *       cookieDays:       90,
 *       excludeCurrentProduct: true,
 *       onProductAdded:   (el, handle) => { … },
 *       onComplete:       (container) => { … },
 *     };
 *   </script>
 *   <script src="{{ 'recently-viewed.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /*  Defaults (overridable via window.RecentlyViewedConfig)             */
  /* ------------------------------------------------------------------ */
  const DEFAULTS = {
    limit: 12,
    maxInMemory: 10,
    containerId: 'recently-viewed-products',
    sectionId: 'api-product-grid-item',
    routesRoot: (window.theme && window.theme.routes && window.theme.routes.root) || '/',
    cookieName: 'shopify_recently_viewed',
    cookieDays: 90,
    excludeCurrentProduct: true,
    onProductAdded: null,
    onComplete: null,
  };

  const cfg = Object.assign({}, DEFAULTS, window.RecentlyViewedConfig || {});

  /* ------------------------------------------------------------------ */
  /*  Storage helpers (localStorage + cookie fallback)                   */
  /*  rv-tracker.liquid and the theme use viewedProducts /              */
  /*  viewedProductHandles in localStorage — read that first.           */
  /* ------------------------------------------------------------------ */
  const LS_IDS = 'viewedProducts';
  const LS_MAP = 'viewedProductHandles';

  const cookie = {
    _expires() {
      const d = new Date();
      d.setTime(d.getTime() + 864e5 * cfg.cookieDays);
      return d.toUTCString();
    },

    read() {
      const match = document.cookie.split('; ').find((c) => c.startsWith(cfg.cookieName + '='));
      if (!match) return [];
      const val = match.split('=')[1];
      return val ? decodeURIComponent(val).split(' ').filter(Boolean) : [];
    },

    write(handles) {
      const val = encodeURIComponent(handles.join(' '));
      const secure = location.protocol === 'https:' ? '; Secure' : '';
      document.cookie =
        `${cfg.cookieName}=${val}; expires=${this._expires()}; path=/; SameSite=Lax${secure}`;
    },

    remove(handle) {
      const list = this.read();
      const idx = list.indexOf(handle);
      if (idx !== -1) {
        list.splice(idx, 1);
        this.write(list);
      }
    },

    destroy() {
      document.cookie =
        `${cfg.cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
    },
  };

  const storage = {
    read() {
      try {
        const ids = JSON.parse(localStorage.getItem(LS_IDS) || '[]');
        const map = JSON.parse(localStorage.getItem(LS_MAP) || '{}');
        const handles = [];
        ids.forEach((id) => {
          const h = map[String(id)];
          if (h && handles.indexOf(h) === -1) handles.push(h);
        });
        Object.keys(map).forEach((id) => {
          const h = map[id];
          if (h && handles.indexOf(h) === -1) handles.push(h);
        });
        if (handles.length) return handles;
      } catch (e) {
        /* localStorage unavailable */
      }
      return cookie.read();
    },

    write(handles) {
      cookie.write(handles);
    },

    remove(handle) {
      cookie.remove(handle);
      try {
        const map = JSON.parse(localStorage.getItem(LS_MAP) || '{}');
        Object.keys(map).forEach((id) => {
          if (map[id] === handle) delete map[id];
        });
        localStorage.setItem(LS_MAP, JSON.stringify(map));
        const ids = JSON.parse(localStorage.getItem(LS_IDS) || '[]');
        localStorage.setItem(
          LS_IDS,
          JSON.stringify(ids.filter((id) => map[String(id)]))
        );
      } catch (e) {
        /* ignore */
      }
    },

    destroy() {
      cookie.destroy();
      try {
        localStorage.removeItem(LS_IDS);
        localStorage.removeItem(LS_MAP);
      } catch (e) {
        /* ignore */
      }
    },
  };

  /* ------------------------------------------------------------------ */
  /*  Core: record a product view                                       */
  /* ------------------------------------------------------------------ */
  function record(handle) {
    if (!handle) {
      if (location.pathname.indexOf('/products/') === -1) return;
      const m = decodeURIComponent(location.pathname).match(/\/products\/([^/?#]+)/);
      if (!m) return;
      handle = m[1];
    }

    const list = storage.read();
    const idx = list.indexOf(handle);

    if (idx !== -1) list.splice(idx, 1);
    list.unshift(handle);
    storage.write(list.slice(0, cfg.maxInMemory));
  }

  /* ------------------------------------------------------------------ */
  /*  Core: render recently viewed products                             */
  /* ------------------------------------------------------------------ */
  function render(options) {
    const opts = Object.assign({}, cfg, options || {});
    const container =
      typeof opts.containerId === 'string'
        ? document.getElementById(opts.containerId)
        : opts.containerId; // also accept a DOM element

    if (!container) return Promise.resolve([]);

    let queue = storage.read();

    if (opts.excludeCurrentProduct) {
      const pm = decodeURIComponent(location.pathname).match(/\/products\/([^/?#]+)/);
      if (pm) queue = queue.filter((h) => h !== pm[1]);
    }

    const toShow = Math.min(queue.length, opts.limit);
    if (!toShow) return Promise.resolve([]);

    const root = opts.routesRoot.replace(/\/$/, '');

    let shown = 0;
    const rendered = [];

    return new Promise((resolve) => {
      (function next() {
        if (!queue.length || shown >= toShow) {
          container.style.removeProperty('display');
          if (typeof opts.onComplete === 'function') opts.onComplete(container, rendered);
          return resolve(rendered);
        }

        const handle = queue.shift();

        fetch(`${root}/products/${handle}?section_id=${opts.sectionId}`)
          .then((r) => r.text())
          .then((html) => {
            if (html.includes('data-unpublished')) {
              storage.remove(handle);
              return next();
            }

            const temp = document.createElement('div');
            temp.innerHTML = html;

            const content = temp.querySelector('[data-api-content]');
            const fragment = content ? content.innerHTML : html;

            container.insertAdjacentHTML('beforeend', fragment);
            const added = container.lastElementChild;
            rendered.push(added);

            if (typeof opts.onProductAdded === 'function') opts.onProductAdded(added, handle);

            shown++;
            next();
          })
          .catch(() => {
            storage.remove(handle);
            next();
          });
      })();
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Public API                                                        */
  /* ------------------------------------------------------------------ */
  const RecentlyViewed = {
    record,
    render,
    getHandles: () => storage.read(),
    clear: () => storage.destroy(),
    removeHandle: (h) => storage.remove(h),
    get hasProducts() {
      return storage.read().length > 0;
    },
  };

  /* ------------------------------------------------------------------ */
  /*  Auto-record on product pages (opt out via data-no-auto-record)    */
  /* ------------------------------------------------------------------ */
  const _currentScript = document.currentScript;
  if (!(_currentScript && _currentScript.hasAttribute('data-no-auto-record'))) {
    if (location.pathname.indexOf('/products/') !== -1) {
      record();
    }
  }

  window.RVCustom = RecentlyViewed;
  document.dispatchEvent(new CustomEvent('RVCustomReady'));
})();
